import { authRefresh, isBackendAuthEnabled } from "@/lib/authApi";
import {
  applyAuthTokens,
  getCognitoAccessToken,
  getRefreshToken,
  getStoredIdpMode,
  isAccessTokenUsable,
} from "@/lib/tokenStore";

/** @type {Set<(data: object) => void>} */
const listeners = new Set();

let refreshInFlight = null;

export function addSessionRefreshListener(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notifyListeners(data) {
  for (const fn of listeners) {
    try {
      fn(data);
    } catch {
      // listener errors must not break refresh
    }
  }
}

function isEntraCookieSession() {
  return getStoredIdpMode() === "entra";
}

/**
 * Exchange refresh token for new access tokens.
 * Entra refresh lives in an httpOnly cookie; Cognito still uses sessionStorage.
 * Dedupes concurrent callers. Returns false when refresh is unavailable or fails.
 */
export function silentRefreshBackendSession() {
  if (!isBackendAuthEnabled()) {
    return Promise.resolve(false);
  }
  const entraCookie = isEntraCookieSession();
  const refreshToken = getRefreshToken();
  if (!entraCookie && !refreshToken) {
    return Promise.resolve(false);
  }
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = authRefresh(entraCookie ? undefined : refreshToken)
    .then((data) => {
      applyAuthTokens(data);
      notifyListeners(data);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/** True when backend auth is on and the stored access token should be renewed. */
export function shouldProactivelyRefreshAccessToken() {
  if (!isBackendAuthEnabled()) return false;
  if (isEntraCookieSession()) {
    const accessToken = getCognitoAccessToken();
    return !accessToken || !isAccessTokenUsable(accessToken);
  }
  if (!getRefreshToken()) return false;
  const accessToken = getCognitoAccessToken();
  return !accessToken || !isAccessTokenUsable(accessToken);
}
